import {Box, HBox} from '@buncha/components/core/view';
import {H3Text, H5Text, H6Text} from '@buncha/components/core/text';
import React, {useCallback, useMemo} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {Analytics} from '@buncha/config/analytics/analytics';
import AndroidIcon from '@buncha/components/core/icon/AndroidIcon';
import AppleIcon from '@buncha/components/core/icon/AppleIcon';
import {BottomButton} from '@buncha/components/composites/Button';
import BottomSheet from '@buncha/components/composites/bottomSheet';
import {CommonScreens} from '@buncha/common/navigation/types';
import {CustomFlatList} from '@buncha/components/core/list';
import {Divider} from 'native-base';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '@buncha/components/conditional/If';
import {MainScreens} from '@buncha/navigation/types';
import {Modules} from '@buncha/utils/types';
import PrinterDeviceIcon from '@buncha/components/core/icon/PrinterDeviceIcon';
import {PrinterNativeModule} from '../../PrinterNativeModule';
import {PrinterSettingBottomSheetStyle} from './styles';
import {SpinnerComponent} from '@buncha/components/core/loader';
import {getOS} from '@buncha/utils/common';
import {navigation} from '@buncha/config/navigation';
import {useGetLocale} from '@buncha/localization/hooks';

interface Props {
  open: boolean;
  onClose: () => void;
}

const PrinterSettingBottomSheet = (props: Props) => {
  const muli = useFontTheme('muli');
  const mortise = useFontTheme('mortise');
  const theme = useColorThemeType('main');
  const {title, body, button} =
    useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};

  const onPressHandler = useCallback(() => {
    Analytics.eventWithProps('show modal', {
      title: 'Printer',
    });
    if (getOS() !== 'ios') PrinterNativeModule.DeviceScanner.disableScan();

    navigation.navigate(MainScreens.Common, {
      screen: CommonScreens.PrinterSetting,
    });
    props?.onClose();
  }, [props]);

  const printerSettingInstructions = useMemo(() => {
    return [
      body?.printerSettingInstructions?.[0],
      body?.printerSettingInstructions?.[1],
      body?.printerSettingInstructions?.[2],
      `${body?.printerSettingInstructions?.[3]}${
        getOS() === 'ios'
          ? body?.printerSettingInstructions?.[5]
          : body?.printerSettingInstructions?.[6]
      }`,
      body?.printerSettingInstructions?.[4],
    ];
  }, [body?.printerSettingInstructions]);

  const deviceSettingInstructions = useMemo(() => {
    return [body?.printerSettings?.[3], body?.printerSettings?.[4]];
  }, [body?.printerSettings]);

  const iosSettingInstructions = useMemo(() => {
    return [...deviceSettingInstructions, body?.printerSettings?.[5]];
  }, [body?.printerSettings, deviceSettingInstructions]);

  const SettingInstruction = useCallback(
    (
      icon: React.ReactNode,
      name: string,
      instructions: string[],
      subtitle?: string,
    ) => {
      return (
        <Box>
          <Divider style={PrinterSettingBottomSheetStyle.divider} />
          <Box>
            <HBox style={PrinterSettingBottomSheetStyle.titleContainer}>
              {icon}
              <H5Text
                style={[
                  {
                    fontFamily: muli?.[700]?.normal,
                    color: theme?.text?.blue[300],
                  },
                  PrinterSettingBottomSheetStyle.title,
                ]}>
                {name}
              </H5Text>
            </HBox>
            <Box>
              <If condition={subtitle}>
                <H6Text style={PrinterSettingBottomSheetStyle.subtitle}>
                  {subtitle}
                </H6Text>
              </If>
              {instructions.map((instruction, index) => {
                return (
                  <HBox
                    style={PrinterSettingBottomSheetStyle.instructionContainer}
                    key={index}>
                    <H6Text>{index + 1}.</H6Text>
                    <H6Text
                      style={PrinterSettingBottomSheetStyle.instructionText}>
                      {instruction}
                    </H6Text>
                  </HBox>
                );
              })}
            </Box>
          </Box>
        </Box>
      );
    },
    [muli, theme?.text?.blue],
  );

  const getDeviceIcons = useMemo(() => {
    if (getOS() === 'android') return <AndroidIcon viewBox="2 -1 25 20" />;
    return <AppleIcon viewBox="2 -1 25 20" />;
  }, []);

  const getInstructions = useMemo(() => {
    if (getOS() === 'android') return deviceSettingInstructions;
    return iosSettingInstructions;
  }, [deviceSettingInstructions, iosSettingInstructions]);

  return (
    <BottomSheet open={props?.open} onClose={props?.onClose}>
      <CustomFlatList
        showsVerticalScrollIndicator={false}
        ListHeaderComponent={
          <>
            <Box style={PrinterSettingBottomSheetStyle.topBox}>
              <H3Text
                style={[
                  {
                    fontFamily: mortise?.[700]?.normal,
                    color: theme?.text?.blue?.[300],
                  },
                  PrinterSettingBottomSheetStyle.title,
                ]}>
                {title?.connectPrinter}
              </H3Text>
              <H5Text
                style={[
                  {
                    fontFamily: muli?.[700]?.normal,
                    color: theme?.text?.gray[100],
                  },
                  PrinterSettingBottomSheetStyle.subtitle,
                ]}>
                {body?.connectPrinter}
              </H5Text>
            </Box>
            {SettingInstruction(
              <PrinterDeviceIcon viewBox="1 -1 25 20" />,
              body?.printerSettings?.[0],
              printerSettingInstructions,
            )}
            {SettingInstruction(
              getDeviceIcons,
              body?.printerSettings?.[1],
              getInstructions,
            )}
          </>
        }
        data={undefined}
        renderItem={undefined}
      />
      <BottomButton
        title={button?.continue}
        onPress={onPressHandler}
        loadingIndicator={<SpinnerComponent />}
        bgColor={theme?.background.green[100]}
      />
    </BottomSheet>
  );
};

export default React.memo(PrinterSettingBottomSheet);
